import { Bob } from "../stores/Bob";
import { makeSpreadsheetLine } from "./spreadSheet";
import { makeTransactionsListLine } from "./transactionsList";
import {
  makeTransactionsPage,
  TransactionViewOutput,
} from "./transactionViewer";
import { CurrencySnapshot } from "./types";

// USDT has 6 decimal places
const USDT_DECIMALS = 6;
const USDT_BASE_UNITS_PER_DOLLAR = 10 ** USDT_DECIMALS;

// Stable payment kind enum matching Rails
export enum StablePaymentKind {
  SOLANA_USDT = 0,
}

export interface OurStablePayment {
  amount_satoshis: number;
  platform_fee_satoshis: number;
  total_deducted_satoshis: number;
  amount_stable: number; // in base units (6 decimals for USDT)
  transaction_identifier: string | null;
  destination_address: string;
  kind: number;
  uuid: string;
  created_at: number;
  currency_snapshot: CurrencySnapshot;
}

// Convert USDT base units to display string
export const stableBaseUnitsToUsd = (baseUnits: number | null): string => {
  if (baseUnits === null || baseUnits === undefined) return "0.00";
  return (baseUnits / USDT_BASE_UNITS_PER_DOLLAR).toFixed(2);
};

// Get the label for stable payment kind
const getStablePaymentKindLabel = (kind: number): string => {
  switch (kind) {
    case StablePaymentKind.SOLANA_USDT:
      return "USDT";
    default:
      return "Stablecoin";
  }
};

// Get explorer URL based on kind
const getExplorerUrl = (
  kind: number,
  transactionId: string | null,
): string | null => {
  if (!transactionId) return null;
  switch (kind) {
    case StablePaymentKind.SOLANA_USDT:
      return `https://solscan.io/tx/${transactionId}`;
    default:
      return null;
  }
};

// Get address explorer URL based on kind
const getAddressExplorerUrl = (
  kind: number,
  address: string,
): string | null => {
  switch (kind) {
    case StablePaymentKind.SOLANA_USDT:
      return `https://solscan.io/account/${address}#transfers`;
    default:
      return null;
  }
};

export const makeSpreadsheetLineForStablePayment = (
  txData: OurStablePayment,
  bob: Bob,
) => {
  const kindLabel = getStablePaymentKindLabel(txData.kind);
  return makeSpreadsheetLine({
    type: `Stable Send (${kindLabel})`,
    status: txData.transaction_identifier ? "Confirmed" : "Pending",
    uuid: txData.uuid,
    createdAtInteger: txData.created_at,
    currencySnapshot: txData.currency_snapshot,
    currency: bob.currencyCode,
    amountSatoshis: txData.amount_satoshis,
    isCredit: false,
    comment: `$${stableBaseUnitsToUsd(txData.amount_stable)} ${kindLabel}`,
    payRequest: null,
    routingFeeSatoshis: null,
    platformFeeSatoshis: txData.platform_fee_satoshis,
    lightningAddress: null,
    onchainTransactionId: txData.transaction_identifier,
    onchainReceivingAddress: txData.destination_address,
    onchainConfirmations: null,
  });
};

export const makeTransactionsListLineForStablePayment = (
  txData: OurStablePayment,
  bob: Bob,
) => {
  const kindLabel = getStablePaymentKindLabel(txData.kind);
  const label = txData.transaction_identifier
    ? `${kindLabel} Sent`
    : `${kindLabel} Sending`;

  return makeTransactionsListLine({
    amountSatoshis: txData.amount_satoshis,
    label: label,
    isConfirmed: !!txData.transaction_identifier,
    createdAtInteger: txData.created_at,
    comment: `$${stableBaseUnitsToUsd(txData.amount_stable)}`,
    currencySnapshot: txData.currency_snapshot,
    currency: bob.currencyCode,
    isCredit: false,
    modelName: "StablePayment",
    uuid: txData.uuid,
    lightningAddress: null,
    zapName: null,
    zapPictureUrl: null,
    onchainTransactionId: txData.transaction_identifier,
    onchainReceivingAddress: txData.destination_address,
    onchainConfirmations: null,
  });
};

export type StablePaymentViewOutput = TransactionViewOutput & {
  transactionId: string | null;
  destinationAddress: string;
  platformFeeSatoshis: number;
  totalDeductedSatoshis: number;
  amountStable: number;
  amountStableFormatted: string;
  kindLabel: string;
  explorerTxUrl: string | null;
  explorerAddressUrl: string | null;
};

export const makeTransactionPageDataForStablePayment = (
  txData: OurStablePayment,
  bob: Bob,
): StablePaymentViewOutput => {
  const kindLabel = getStablePaymentKindLabel(txData.kind);
  const header = txData.transaction_identifier
    ? `${kindLabel} Sent`
    : `${kindLabel} Sending`;

  const baseData = makeTransactionsPage({
    header: header,
    uuid: txData.uuid,
    createdAtInteger: txData.created_at,
    currencySnapshot: txData.currency_snapshot,
    currency: bob.currencyCode,
    amountSatoshis: txData.amount_satoshis,
    isCredit: false,
    comment: `$${stableBaseUnitsToUsd(txData.amount_stable)} ${kindLabel}`,
    payRequest: null,
    routingFeeSatoshis: null,
    platformFeeSatoshis: txData.platform_fee_satoshis,
    lightningAddress: null,
  });

  return {
    ...baseData,
    transactionId: txData.transaction_identifier,
    destinationAddress: txData.destination_address,
    platformFeeSatoshis: txData.platform_fee_satoshis,
    totalDeductedSatoshis: txData.total_deducted_satoshis,
    amountStable: txData.amount_stable,
    amountStableFormatted: `$${stableBaseUnitsToUsd(txData.amount_stable)}`,
    kindLabel: kindLabel,
    explorerTxUrl: getExplorerUrl(txData.kind, txData.transaction_identifier),
    explorerAddressUrl: getAddressExplorerUrl(
      txData.kind,
      txData.destination_address,
    ),
  };
};
